import React, { useState, useEffect, Suspense, lazy } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import { HelmetProvider } from 'react-helmet-async';
import AOS from 'aos';
import 'aos/dist/aos.css';
import 'bootstrap/dist/css/bootstrap.min.css';
import 'slick-carousel/slick/slick.css';
import 'slick-carousel/slick/slick-theme.css';
import './index.css';

import { AuthProvider, useAuth } from './context/AuthContext';
import CookieConsent from './components/public/CookieConsent';

// ─── Layouts ─────────────────────────────────────────────────────────────────
import PublicLayout from './components/public/PublicLayout';
import AdminLayout  from './components/admin/AdminLayout';

// ─── Public pages (lazy loaded for performance) ───────────────────────────────
const HomePage       = lazy(() => import('./pages/public/HomePage'));
const ProductsPage   = lazy(() => import('./pages/public/ProductsPage'));
const CareersPage    = lazy(() => import('./pages/public/CareersPage'));
const TeamPage       = lazy(() => import('./pages/public/TeamPage'));
const { OffersPage, ContactPage, AboutPage, InquiryPage,
        ProductDetailPage } = require('./pages/public/OtherPages');
const NoviqoERPPage  = lazy(() => import('./pages/public/NoviqoERPPage'));
const RightIOTPage        = lazy(() => import('./pages/public/RightIOTPage'));
const RightFoodAuditPage  = lazy(() => import('./pages/public/RightFoodAuditPage'));
const ChefmaxPage         = lazy(() => import('./pages/public/ChefmaxPage'));
const SupportQueryPage    = lazy(() => import('./pages/public/SupportQueryPage'));
const TestimonialsPage    = lazy(() => import('./pages/public/TestimonialsPage'));

// ─── Admin pages ──────────────────────────────────────────────────────────────
const AdminLogin         = lazy(() => import('./pages/admin/AdminLogin'));
const Dashboard          = lazy(() => import('./pages/admin/Dashboard'));
const InquiriesAdmin     = lazy(() => import('./pages/admin/InquiriesAdmin'));
const UsersAdmin         = lazy(() => import('./pages/admin/UsersAdmin'));
const TestimonialsAdmin  = lazy(() => import('./pages/admin/TestimonialsAdmin'));
const { SlidersAdmin, ProductsAdmin, OffersAdmin,
        SubscribersAdmin, SettingsAdmin } = require('./pages/admin/AdminPages');
const CareersAdmin   = lazy(() => import('./pages/admin/CareersAdmin'));
const TeamAdmin      = lazy(() => import('./pages/admin/TeamAdmin'));

// ─── Loader ───────────────────────────────────────────────────────────────────
// Smart product route — shows NoviqoERPPage for ERP product, generic page for others
function ProductRouteSelector() {
  const { useParams } = require('react-router-dom');
  const { slug } = useParams();
  const ERP_SLUGS = ['noviqo-erp-solution','nexus-erp-solution','right-erp-solution','erp-solution','noviqo-erp','nexus-erp','right-erp','erp'];
  const IOT_SLUGS = ['right-iot-solution','right-iot','iot-solution','iot'];
  if (IOT_SLUGS.some(s => slug && slug.toLowerCase().includes('iot'))) return <RightIOTPage />;
  const FOOD_SLUGS = ['right-food-audit','food-audit','right-food'];
  if (FOOD_SLUGS.some(s => slug && slug.toLowerCase().includes('food-audit'))) return <RightFoodAuditPage />;
  if (slug && slug.toLowerCase().includes('chefmax')) return <ChefmaxPage />;
  if (ERP_SLUGS.some(s => slug && slug.toLowerCase().includes('erp'))) {
    return <NoviqoERPPage />;
  }
  return <ProductDetailPage />;
}

function PageLoader() {
  return (
    <div style={{ height: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'var(--navy)' }}>
      <div style={{ textAlign: 'center' }}>
        <div style={{ width: 44, height: 44, border: '3px solid rgba(0,212,255,0.15)', borderTopColor: 'var(--accent)', borderRadius: '50%', animation: 'spin 0.7s linear infinite', margin: '0 auto 12px' }} />
        <p style={{ color: 'var(--accent)', fontSize: '0.8rem', fontFamily: 'var(--font-mono)', letterSpacing: '0.1em' }}>Loading...</p>
      </div>
    </div>
  );
}

// ─── Admin route guard ────────────────────────────────────────────────────────
function AdminRoute({ children }) {
  const { isAuthenticated, isAdmin, loading } = useAuth();
  if (loading) return <PageLoader />;
  if (!isAuthenticated) return <Navigate to="/admin/login" replace />;
  if (!isAdmin)         return <Navigate to="/" replace />;
  return children;
}

function SuperAdminRoute({ children }) {
  const { isAuthenticated, isSuperAdmin, loading } = useAuth();
  if (loading) return <PageLoader />;
  if (!isAuthenticated) return <Navigate to="/admin/login" replace />;
  if (!isSuperAdmin)    return <Navigate to="/admin/dashboard" replace />;
  return children;
}

function AppRoutes() {
  useEffect(() => {
    AOS.init({ duration: 650, once: true, easing: 'ease-out-cubic', offset: 50 });
    const style = document.createElement('style');
    style.textContent = '@keyframes spin { to { transform: rotate(360deg); } }';
    document.head.appendChild(style);
  }, []);

  return (
    <Suspense fallback={<PageLoader />}>
      <Routes>
        {/* ── Public ── */}
        <Route element={<PublicLayout />}>
          <Route path="/"               element={<HomePage />} />
          <Route path="/products"       element={<ProductsPage />} />
          <Route path="/products/right-iot-solution" element={<RightIOTPage />} />
          <Route path="/products/right-food-audit-system" element={<RightFoodAuditPage />} />
          <Route path="/products/chefmax-web-suite" element={<ChefmaxPage />} />
          {/* Old ERP branding — redirect to the new canonical NOVIQO ERP URL */}
          <Route path="/products/nexus-erp-solution" element={<Navigate to="/products/noviqo-erp-solution" replace />} />
          <Route path="/products/right-erp-solution" element={<Navigate to="/products/noviqo-erp-solution" replace />} />
          <Route path="/products/:slug" element={<ProductRouteSelector />} />
          <Route path="/careers"       element={<CareersPage />} />
          <Route path="/team"           element={<TeamPage />} />
          <Route path="/offers"         element={<OffersPage />} />
          <Route path="/about"          element={<AboutPage />} />
          <Route path="/contact"        element={<ContactPage />} />
          <Route path="/inquiry"        element={<InquiryPage />} />
          <Route path="/testimonials"    element={<TestimonialsPage />} />
          <Route path="/support"          element={<SupportQueryPage />} />
        </Route>

        {/* ── Admin ── */}
        <Route path="/admin/login" element={<AdminLogin />} />
        <Route path="/admin" element={<AdminRoute><AdminLayout /></AdminRoute>}>
          <Route index                  element={<Navigate to="dashboard" replace />} />
          <Route path="dashboard"       element={<Dashboard />} />
          <Route path="inquiries"       element={<InquiriesAdmin />} />
          <Route path="products"        element={<SuperAdminRoute><ProductsAdmin /></SuperAdminRoute>} />
          <Route path="careers"         element={<CareersAdmin />} />
          <Route path="team"            element={<TeamAdmin />} />
          <Route path="offers"          element={<OffersAdmin />} />
          <Route path="sliders"         element={<SlidersAdmin />} />
          <Route path="testimonials"    element={<TestimonialsAdmin />} />
          <Route path="subscribers"     element={<SubscribersAdmin />} />
          <Route path="users"           element={<UsersAdmin />} />
          <Route path="settings"        element={<SuperAdminRoute><SettingsAdmin /></SuperAdminRoute>} />
        </Route>

        {/* Redirects for bare product slugs Google may have indexed without /products/ prefix */}
        <Route path="/right-food-audit-system"  element={<Navigate to="/products/right-food-audit-system" replace />} />
        <Route path="/right-iot-solution"       element={<Navigate to="/products/right-iot-solution" replace />} />
        <Route path="/chefmax-web-suite"         element={<Navigate to="/products/chefmax-web-suite" replace />} />
        <Route path="/noviqo-erp-solution"       element={<Navigate to="/products/noviqo-erp-solution" replace />} />
        <Route path="/nexus-erp-solution"        element={<Navigate to="/products/noviqo-erp-solution" replace />} />

        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </Suspense>
  );
}

const HERO_IMAGES = [
  '/images/sliders/slide-1-innovation.jpg',
  '/images/sliders/slide-2-healthcare.jpg',
  '/images/sliders/slide-3-security.jpg',
  '/images/sliders/slide-4-iot.jpg',
  '/images/sliders/slide-5-digital.jpg',
];

function Preloader() {
  const [hiding, setHiding]   = useState(false);
  const [gone, setGone]       = useState(false);
  const [progress, setProgress] = useState(0);

  useEffect(() => {
    const total = HERO_IMAGES.length;
    let loaded  = 0;
    let settled = false;
    let minDone = false;
    let imgsDone = false;

    const tryHide = () => {
      if (settled || !minDone || !imgsDone) return;
      settled = true;
      setHiding(true);
      setTimeout(() => setGone(true), 400);
    };

    // Minimum display: 800ms so branding always shows
    const minTimer = setTimeout(() => { minDone = true; tryHide(); }, 800);

    // Hard cap: never block longer than 6s
    const maxTimer = setTimeout(() => {
      if (settled) return;
      settled = true;
      setHiding(true);
      setTimeout(() => setGone(true), 400);
    }, 6000);

    const onSettled = () => {
      loaded++;
      setProgress(Math.round((loaded / total) * 100));
      if (loaded >= total) {
        imgsDone = true;
        clearTimeout(maxTimer);
        tryHide();
      }
    };

    HERO_IMAGES.forEach(src => {
      const img = new Image();
      img.onload  = onSettled;
      img.onerror = onSettled; // don't block on broken images
      img.src = src;
    });

    return () => { clearTimeout(minTimer); clearTimeout(maxTimer); settled = true; };
  }, []);

  if (gone) return null;

  return (
    <div style={{
      position: 'fixed', inset: 0, zIndex: 99999,
      background: 'linear-gradient(135deg, #060d1a 0%, #0d1829 50%, #091428 100%)',
      display: 'flex', flexDirection: 'column',
      alignItems: 'center', justifyContent: 'center', gap: 28,
      opacity: hiding ? 0 : 1,
      transition: 'opacity 0.4s ease',
      pointerEvents: hiding ? 'none' : 'all',
    }}>
      <style>{`
        .preloader-logo {
          filter: brightness(0) invert(1);
        }
        @keyframes dot-pulse {
          0%,80%,100% { opacity: 0.3; transform: scale(0.8); }
          40% { opacity: 1; transform: scale(1.2); }
        }
      `}</style>

      {/* Logo */}
      <img
        src="/images/logo/softlogic-logo.webp"
        alt="Softlogic Australia"
        className="preloader-logo"
        style={{ height: 100, maxWidth: 300, objectFit: 'contain' }}
      />

      {/* Tagline */}
      <div style={{ fontSize:'0.7rem', fontWeight:700, letterSpacing:'0.2em', textTransform:'uppercase', color:'rgba(0,212,255,0.7)' }}>
        Best in the Business
      </div>

      {/* Progress bar — driven by actual image load progress */}
      <div style={{ width:200, height:2, background:'rgba(255,255,255,0.08)', borderRadius:2, overflow:'hidden' }}>
        <div style={{ height:'100%', borderRadius:2, background:'linear-gradient(90deg,#0066ff,#00d4ff)', width:`${progress}%`, transition:'width 0.3s ease' }} />
      </div>

      {/* Dots */}
      <div style={{ display:'flex', gap:8 }}>
        {[0,1,2].map(i => (
          <div key={i} style={{ width:6, height:6, borderRadius:'50%', background:'#00d4ff', animation:`dot-pulse 1.2s ease-in-out ${i*0.2}s infinite` }} />
        ))}
      </div>
    </div>
  );
}

export default function App() {
  return (
    <HelmetProvider>
      <Preloader />
      <CookieConsent />
      <BrowserRouter>
        <AuthProvider>
          <Toaster
            position="top-right"
            toastOptions={{
              duration: 3500,
              style: {
                background: 'var(--navy-mid)',
                color: 'white',
                border: '1px solid rgba(0,212,255,0.2)',
                fontFamily: 'var(--font-body)',
                borderRadius: '10px',
                fontSize: '0.9rem',
              },
              success: { iconTheme: { primary: 'var(--accent)', secondary: 'var(--navy)' } },
              error:   { iconTheme: { primary: '#e74c3c',       secondary: 'var(--navy)' } },
            }}
          />
          <AppRoutes />
        </AuthProvider>
      </BrowserRouter>
    </HelmetProvider>
  );
}
